import React from 'react';
import { Link } from 'react-router-dom';
import { animated, useTransition } from '@react-spring/web';

import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAnimatedRouteState } from '@wildidea/components/AnimatedRoutes/AnimatedRoutes';
import { Card } from '@wildidea/components/Card/Card';
import { useThemeAnimations } from '@wildidea/components/Theme/Theme';

import { getPost, NEWS_POSTS, type NewsPost } from './posts/index.js';
import { PostVideo } from './PostVideo.js';
import { renderMarkdown } from './renderMarkdown.js';
import { usePageMeta } from './usePageMeta.js';

import * as css from './NewsRoute.module.css';

import 'github-markdown-css';

const SITE_ORIGIN = 'https://rescueally.org';

const formatPostDate = (isoDate: string): string =>
  new Date(`${isoDate}T12:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC'
  });

/** Phone-aware store / web-checkout redirect on the marketing site, tagged per post. */
const getUrl = (slug: string): string =>
  `${SITE_ORIGIN}/get?utm_source=website&utm_medium=news&utm_campaign=${slug}&utm_content=get`;

export interface NewsRouteProps {
  /**
   * When set, the feed scrolls to this post on load and the page meta is that
   * post's. Each `/news/<slug>` is a literal route in App.tsx so the prerender
   * and CloudFront route lists can name it.
   */
  slug?: string;
}

/**
 * The news feed: every post in full, newest first. Posts are short (the
 * win-back email copy), so there is no index/detail split; `/news/<slug>` is
 * the same page scrolled to that post, which is what ads and social posts
 * link to.
 */
const NewsRoute: React.FC<NewsRouteProps> = ({ slug }) => {
  const focused = slug ? getPost(slug) : undefined;
  if (slug && !focused) throw new Error(`Unknown news post: ${slug}`);

  const { phase, onTransitionEnd } = useAnimatedRouteState();
  const active = phase === 'enter' || phase === 'active';
  const { fromBelow } = useThemeAnimations('Common');
  const contentTransition = useTransition(active, { ...fromBelow, onRest: onTransitionEnd });

  const newest = NEWS_POSTS[0];
  const metaPost = focused ?? newest;
  usePageMeta({
    title: focused ? focused.title : 'News',
    description: focused ? focused.description : `News from Rescue Ally. Latest: ${newest.title}.`,
    image: `${SITE_ORIGIN}${metaPost.share ?? metaPost.hero ?? metaPost.video?.poster ?? '/share-home.jpg'}`,
    url: `${SITE_ORIGIN}/news${focused ? `/${focused.slug}` : ''}`,
    type: focused ? 'article' : 'website'
  });

  // Scroll to the linked post once the route is settled (the enter animation
  // translates the card, so scrolling mid-transition lands in the wrong spot).
  React.useEffect(
    function scrollToPost() {
      if (!focused || phase !== 'active') return;
      document.getElementById(focused.slug)?.scrollIntoView({ block: 'start' });
    },
    [focused, phase]
  );

  return contentTransition((style, active) =>
    active ? (
      <animated.div style={style} className={css.layout}>
        <Card
          color="transparent"
          className={css.contentCard}
          header={
            <div className={[css.header, 'h1-margin'].join(' ')}>
              <Link to="/" aria-label="Back">
                <FontAwesomeIcon icon={faArrowLeft} />
              </Link>
              <h1>News</h1>
            </div>
          }
        >
          <div className={css.feed}>
            {NEWS_POSTS.map((post) => (
              <Post key={post.slug} post={post} />
            ))}
          </div>
        </Card>
      </animated.div>
    ) : null
  );
};

const Post: React.FC<{ post: NewsPost }> = ({ post }) => {
  const html = React.useMemo(() => renderMarkdown(post.markdown), [post.markdown]);
  return (
    <article id={post.slug} className={css.post}>
      <Card
        color="paper1"
        className={css.postCard}
        figure={
          post.video ? (
            <PostVideo src={post.video.src} poster={post.video.poster} label={post.video.label} />
          ) : post.hero ? (
            <img className={css.hero} src={post.hero} alt={post.heroAlt ?? ''} />
          ) : undefined
        }
        fullBleed={Boolean(post.video || post.hero)}
        orientation="portrait"
        density="relaxed"
        elevated
      >
        <header>
          <time dateTime={post.date} className={css.postDate}>
            {formatPostDate(post.date)}
          </time>
          <h2>
            <Link to={`/news/${post.slug}`}>{post.title}</Link>
          </h2>
        </header>
        <div className={css.body} dangerouslySetInnerHTML={{ __html: html }} />
        <div className={css.cta}>
          <a className={css.ctaButton} href={getUrl(post.slug)}>
            Get Rescue Ally
          </a>
        </div>
      </Card>
    </article>
  );
};

export default NewsRoute;
